import { useCallback, useEffect, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { useOfferDayPartAvailability } from 'experiments/deeplinks-flow-feature/treatment/features/offer/hooks/use-offer-day-part-availability';
import { applyStoreRestrictionUnavailableMessage } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/apply-store-restriction-unavailable-message';
import {
  LoyaltyServiceOfferRedemptionType,
  useLoyaltyServiceOfferQuery,
} from 'generated/graphql-gateway';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { LoyaltyEngineId, appOfferToLoyaltyOffer } from 'state/loyalty/types';
import { useServiceModeLive } from 'state/service-mode';
import { logger } from 'utils/logger';

import { mapServiceOffer } from '../utils';
import { getLoyaltyServiceServiceMode } from '../utils/service-mode';

export function useLoyaltyServiceOffer({
  loyaltyEngineId,
  skip = false,
  includeStoreRestricted = false,
  omitStoreId = false,
}: {
  loyaltyEngineId: LoyaltyEngineId;
  skip?: boolean;
  /**
   * Deeplink recovery only: request store-restricted offers and mark them
   * unavailable locally so they can render as a card in the offers feed.
   */
  includeStoreRestricted?: boolean;
  /**
   * Deeplink recovery only: query without the selected restaurant so an offer
   * that store cannot redeem still resolves its display content. Availability
   * is still evaluated against the selected store locally.
   */
  omitStoreId?: boolean;
}) {
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();
  const { loading: loyaltyUserIdLoading } = useLoyaltyUserId();
  const storeId = store?.number;
  const querySkip = skip || !loyaltyEngineId || loyaltyUserIdLoading;

  const { data, error, loading: queryLoading, refetch, called } = useLoyaltyServiceOfferQuery({
    variables: {
      id: loyaltyEngineId,
      serviceMode: getLoyaltyServiceServiceMode(serviceMode),
      storeId: omitStoreId ? undefined : storeId,
      redemptionTypes: [LoyaltyServiceOfferRedemptionType.STANDARD],
      includeOfferGroups: true,
      omitInvalids: false,
      ...(includeStoreRestricted ? { rulesToSkip: { storeRestrictions: true } } : {}),
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: querySkip,
  });

  const { getDayPartUnavailableMessage } = useOfferDayPartAvailability();

  const offerListItem = useMemo(() => {
    const serviceOffer = data?.loyaltyServiceOffer;
    if (!serviceOffer) {
      return undefined;
    }

    const mappedOffer = mapServiceOffer(serviceOffer);
    if (!includeStoreRestricted) {
      return mappedOffer;
    }

    const storeRestrictedOffer = applyStoreRestrictionUnavailableMessage(mappedOffer, storeId);
    if (storeRestrictedOffer.inlineAlertMessage) {
      return storeRestrictedOffer;
    }

    // A daypart-limited offer is not store-restricted, so it resolves as an
    // ordinary offer. Naming its dayparts here is what stops the detail modal
    // and sends the guest to the feed card instead.
    const dayPartMessage = getDayPartUnavailableMessage(storeRestrictedOffer);
    return dayPartMessage
      ? { ...storeRestrictedOffer, inlineAlertMessage: dayPartMessage }
      : storeRestrictedOffer;
  }, [data, getDayPartUnavailableMessage, includeStoreRestricted, storeId]);

  const selectedIncentive = useMemo(
    () => (offerListItem ? appOfferToLoyaltyOffer(offerListItem) : undefined),
    [offerListItem]
  );

  useEffect(() => {
    if (error) {
      logger.warn({ error, message: 'Failed to load loyalty-service offer', loyaltyEngineId });
    }
  }, [error, loyaltyEngineId]);

  const refreshOffer = useCallback(async () => {
    if (querySkip) {
      return;
    }
    try {
      await refetch();
    } catch (error) {
      logger.warn({ error, message: 'Failed to load loyalty-service offer', loyaltyEngineId });
    }
  }, [loyaltyEngineId, querySkip, refetch]);

  const isIncentivesLoading =
    !skip &&
    Boolean(loyaltyEngineId) &&
    (loyaltyUserIdLoading || queryLoading || (!querySkip && !called));
  // A completed request with an Apollo/network error is not evidence that the
  // offer is missing. Callers use `hasSettled && !offer` as the unavailable
  // signal, so keep failures separate from legitimate empty responses.
  const hasSettled = !querySkip && !queryLoading && called && !error;

  return {
    selectedIncentive,
    offerListItem,
    isIncentivesLoading,
    hasSettled,
    queryError: error,
    refreshOffer,
  };
}
